import type SignatureCanvasType from 'react-signature-canvas';

export function isCanvasEmpty(canvas: SignatureCanvasType | null | undefined): boolean {
  if (!canvas) return true;
  try { return canvas.isEmpty(); } catch { return true; }
}

/** Full `data:image/png;base64,...` URL for an in-memory preview, or null when empty. */
export function canvasToDataUrl(canvas: SignatureCanvasType | null | undefined): string | null {
  if (!canvas || isCanvasEmpty(canvas)) return null;
  try { return canvas.toDataURL('image/png'); } catch { return null; }
}

function elementToPngBlob(el: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      el.toBlob((blob) => resolve(blob), 'image/png');
    } catch {
      resolve(null);
    }
  });
}

/** The PNG bytes v2 stages as a `signature` upload. Never base64 over the wire. */
export async function canvasToPngBlob(canvas: SignatureCanvasType | null | undefined): Promise<Blob | null> {
  if (!canvas || isCanvasEmpty(canvas)) return null;
  try {
    const blob = await elementToPngBlob(canvas.getCanvas());
    if (blob) return blob;
  } catch { /* fall through to the data URL path */ }
  const dataUrl = canvasToDataUrl(canvas);
  if (!dataUrl) return null;
  try {
    return await (await fetch(dataUrl)).blob();
  } catch {
    return null;
  }
}

/** Smaller PNG (bounding box only) — the retry when the full canvas exceeds the size cap. */
export async function trimmedCanvasToPngBlob(canvas: SignatureCanvasType | null | undefined): Promise<Blob | null> {
  if (!canvas || isCanvasEmpty(canvas)) return null;
  try {
    return await elementToPngBlob(canvas.getTrimmedCanvas());
  } catch {
    return null;
  }
}
